/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type { BibleBookId, BibleManifest } from "@packages/sdk";
import type { ThemeType } from "@packages/ui";
import {
  createModal,
  Modal,
  ModalTabContainer,
  ModalTabContent,
  numericPercentages,
  spacing,
  stroke,
  useDynamicViewport,
  useModal,
} from "@packages/ui";
import { useEffect, useState } from "react";

import {
  BIBLE_DESKTOP_WIDTH,
  isCustomDesktop,
  isLargeDevice,
} from "../bibleStyle.constants";
import type { RtlOrderFn } from "./BibleNavigation";
import { BookNavigator } from "./BookNavigator";
import { ChapterNavigator } from "./ChapterNavigator";

const firstMenuZoneStyle: Interpolation<ThemeType> = (theme) => ({
  borderRight: 0,
  [isCustomDesktop]: {
    borderRight: `${stroke.regular} solid ${theme.semanticColors.onOverlayLow}`,
  },
});
const menuStyle: Interpolation = {
  display: "grid",
  gridTemplateColumns: "1fr",
  gridTemplateRows: "min-content min-content",
  maxHeight: "60vh",
  [isCustomDesktop]: {
    gridTemplateColumns: "1fr 1fr",
    gridTemplateRows: "1fr",
  },
};
const menuZoneStyle: Interpolation = {
  padding: 0,
  width: spacing.full,
  [isCustomDesktop]: {
    padding: `${spacing.xl} ${spacing.xxl}`,
    width: "revert",
  },
};
const modalStyle: Interpolation = {
  marginRight: 0,
  width: spacing.full,
  [isCustomDesktop]: {
    marginRight: spacing.m,
    maxWidth: "revert",
    width: "max-content",
  },
  [isLargeDevice]: {
    width: "max-content",
  },
};

const scrollStyle: Interpolation = {
  maxHeight: "60vh",
  maxWidth: numericPercentages[100],
  [isCustomDesktop]: { maxWidth: "max(calc(7 * 64px + 2 * 48px), 33vw)" },
  [isLargeDevice]: { maxWidth: "max(calc(7 * 64px), 50vw)" },
};

type Props = {
  manifest: BibleManifest;
  book: BibleBookId;
};

export const BibleNavBooks = createModal<Props>(({ manifest, book }) => {
  const modal = useModal();
  const [navBook, setNavBook] = useState<BibleBookId>(book);
  const isDesktop = useDynamicViewport({ minimumWidth: BIBLE_DESKTOP_WIDTH });
  const analytics = useAnalytics();
  const language = manifest.translation_language;

  useEffect(() => {
    if (modal.visible) {
      analytics.track({
        event: HallowAnalyticsEvent.OpenedBibleBookMenu,
      });
      if (isDesktop) {
        analytics.track({
          event: HallowAnalyticsEvent.OpenedBibleChapterMenu,
        });
      }
    }
  }, [modal.visible, isDesktop]);

  const rtlOrder: RtlOrderFn = (itemNum: number, numInRow: number) => {
    let modulo = (itemNum % numInRow) - 1;
    const multiple = Math.ceil(itemNum / numInRow) * numInRow;
    if (modulo < 0) modulo += numInRow;
    return (multiple - modulo).toString();
  };

  const chaptersJsx = (
    <ChapterNavigator
      manifest={manifest}
      navBook={navBook}
      rtlOrder={rtlOrder}
      language={language}
      css={[scrollStyle, menuZoneStyle]}
    />
  );

  const booksJsx = (
    <BookNavigator
      manifest={manifest}
      rtlOrder={rtlOrder}
      language={language}
      navBook={navBook}
      setNavBook={setNavBook}
      css={[scrollStyle, menuZoneStyle]}
    />
  );

  return (
    <Modal
      contentCSS={modalStyle}
      open={modal.visible}
      onOpenChange={(open) => (open ? modal.show() : modal.remove())}
    >
      {isDesktop ? (
        <div css={menuStyle}>
          <div css={firstMenuZoneStyle}>{booksJsx}</div>
          <div>{chaptersJsx}</div>
        </div>
      ) : (
        <ModalTabContainer defaultValue={"book"}>
          <ModalTabContent value={"book"}>{booksJsx}</ModalTabContent>
          <ModalTabContent value={"chapter"}>{chaptersJsx}</ModalTabContent>
        </ModalTabContainer>
      )}
    </Modal>
  );
});
